import React, { useEffect, useRef } from 'react';
import { SwatchBackground, themeSelectorStyles } from './style';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import Input from '@mui/material/Input';
import Box from '@mui/material/Box';
import { SelectChangeEvent } from '@mui/material/Select';
import { useTranslation } from 'react-i18next';
import usePractitionerControlsContext from '../../hooks/usePractitionerControlsContext/usePractitionerControlsContext';
import useAppContext from '../../hooks/useAppContext/useAppContext';
import { Theme } from '../PhaserApp/enums/Themes';
import { PSYLARIS_LIGHT_THEME_BACKGROUND } from '../../utils/Colors';
import Tooltip from '../Tooltip/Tooltip';
import muiTheme from '../../theme';
import { HEADER_CONTROL_HEIGHT, HEADER_CONTROL_WIDTH } from '../UI/headerControl';
import useDevicePixelRatio from '../../hooks/useDevicePixelRatio/useDevicePixelRatio';

const SPRITE_FRAME_SIZE = 512;

/** The object leaves the surrounding ground visible — the ground is what names the theme. */
const SWATCH_OBJECT_RATIO = 0.8;
/** Menu previews are enlarged so the choice can actually be verified before committing. */
const MENU_SWATCH_SCALE = 1.5;
/** Default theme leaves the page background showing through, so that is what it previews. */
const DEFAULT_THEME_BACKGROUND = muiTheme.palette?.background?.default ?? '#303030';

interface ThemeSelectorProps {
  size?: 'small' | 'large';
}

type TintGradient = { from: string; to: string };

interface ThemeOption {
  key: string;
  sprite: string;
  value: Theme;
  background: SwatchBackground;
  tint?: TintGradient;
  overlaySprite?: string;
}

/** Canvas 2D has no mipmaps, so halve step by step — what `mipmapFilter` does for the game's canvas. */
function reduceFrame(img: HTMLImageElement, target: number): HTMLCanvasElement | null {
  let source: CanvasImageSource = img;
  let size = SPRITE_FRAME_SIZE;
  let step: HTMLCanvasElement;

  do {
    const next = Math.max(target, Math.floor(size / 2));
    step = document.createElement('canvas');
    step.width = next;
    step.height = next;
    const ctx = step.getContext('2d');
    if (!ctx) return null;
    ctx.imageSmoothingQuality = 'high';
    // First pass reads frame 0 out of the 16-frame sheet; later passes read the whole step.
    ctx.drawImage(source, 0, 0, size, size, 0, 0, next, next);
    source = step;
    size = next;
  } while (size > target);

  return step;
}

const SpriteIcon: React.FC<{ src: string; size?: number; tint?: TintGradient; overlaySrc?: string }> = ({
  src,
  size = 20,
  tint,
  overlaySrc,
}) => {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const ratio = useDevicePixelRatio();

  useEffect(() => {
    let cancelled = false;

    const render = (baseImg: HTMLImageElement, overlayImg?: HTMLImageElement) => {
      const canvas = canvasRef.current;
      if (!canvas) return;
      const ctx = canvas.getContext('2d');
      if (!ctx) return;
      const buffer = Math.max(1, Math.round(size * ratio));
      const base = reduceFrame(baseImg, buffer);
      if (!base) return;

      canvas.width = buffer;
      canvas.height = buffer;
      ctx.clearRect(0, 0, buffer, buffer);
      ctx.globalCompositeOperation = 'source-over';
      ctx.drawImage(base, 0, 0);

      if (tint) {
        // Multiply blend keeps the body's highlights/shading while infusing color.
        ctx.globalCompositeOperation = 'multiply';
        const grad = ctx.createLinearGradient(0, 0, 0, buffer);
        grad.addColorStop(0, tint.from);
        grad.addColorStop(1, tint.to);
        ctx.fillStyle = grad;
        ctx.fillRect(0, 0, buffer, buffer);
        // Re-mask to the body's alpha so transparent pixels stay transparent.
        ctx.globalCompositeOperation = 'destination-in';
        ctx.drawImage(base, 0, 0);
        ctx.globalCompositeOperation = 'source-over';
      }

      // The fixed overlay (e.g. the penguin's face) sits on top and keeps its own colours.
      if (overlayImg) {
        const overlay = reduceFrame(overlayImg, buffer);
        if (overlay) {
          ctx.globalCompositeOperation = 'source-over';
          ctx.drawImage(overlay, 0, 0);
        }
      }
    };

    const baseImg = new Image();
    let overlayImg: HTMLImageElement | undefined;
    let pending = overlaySrc ? 2 : 1;
    const onReady = () => {
      if (cancelled) return;
      pending -= 1;
      if (pending === 0) render(baseImg, overlayImg);
    };

    baseImg.onload = onReady;
    baseImg.src = src;

    if (overlaySrc) {
      overlayImg = new Image();
      overlayImg.onload = onReady;
      overlayImg.src = overlaySrc;
    }

    return () => {
      cancelled = true;
    };
  }, [src, size, ratio, tint?.from, tint?.to, overlaySrc]);

  return <canvas className="icon" ref={canvasRef} width={size} height={size} style={{ width: size, height: size }} />;
};

/** A miniature of the client's scene: the theme's background with its own object on it. */
const ThemeSwatch: React.FC<{ option: ThemeOption; width: number }> = ({ option, width }) => {
  const height = Math.round((width / HEADER_CONTROL_WIDTH) * HEADER_CONTROL_HEIGHT);
  const objectSize = Math.round(height * SWATCH_OBJECT_RATIO);

  return (
    <Box sx={themeSelectorStyles.swatch(option.background, width, height)}>
      <SpriteIcon src={option.sprite} size={objectSize} tint={option.tint} overlaySrc={option.overlaySprite} />
    </Box>
  );
};

const themeOptions: ThemeOption[] = [
  {
    key: 'default',
    // The ball keeps its own silver: its colour is a separate control the practitioner
    // sets independently, so tinting it here would signal the wrong setting.
    sprite: 'assets/phaser/images/Silver_16_flat_8192x512_3.png',
    value: Theme.Default,
    background: { color: DEFAULT_THEME_BACKGROUND },
  },
  {
    key: 'child',
    // Body sheet (tinted) + fixed face sheet drawn on top, matching the in-game child target.
    sprite: 'assets/phaser/images/penguin_owl_tint_8192x512.png',
    overlaySprite: 'assets/phaser/images/penguin_owl_fixed_8192x512.png',
    value: Theme.Child,
    tint: { from: '#A7CBF0', to: '#6E9FD4' },
    background: { image: '/assets/phaser/images/child_bg_emptylandscape.png' },
  },
  {
    key: 'light',
    sprite: 'assets/phaser/images/Silver_16_flat_8192x512_3.png',
    value: Theme.Light,
    background: { color: PSYLARIS_LIGHT_THEME_BACKGROUND },
  },
];

const ThemeSelector: React.FC<ThemeSelectorProps> = ({ size = 'small' }) => {
  const { t } = useTranslation();
  const { theme, setTheme } = usePractitionerControlsContext();
  const { hasDevice } = useAppContext();

  // The light background is a web-only variant — never offer it when an EMDR-VR device is present.
  const visibleOptions = themeOptions.filter((o) => o.value !== Theme.Light || !hasDevice);

  const keyByTheme: Record<number, string> = { [Theme.Child]: 'child', [Theme.Light]: 'light' };
  const selectedKey: string = keyByTheme[theme] ?? 'default';
  const triggerWidth = size === 'large' ? Math.round(HEADER_CONTROL_WIDTH * 1.3) : HEADER_CONTROL_WIDTH;
  const menuWidth = Math.round(HEADER_CONTROL_WIDTH * MENU_SWATCH_SCALE);
  // Name the control and say what it does — both halves already exist as translations.
  const tooltip = `${t('theme')} — ${t('theme-tooltip')}`;

  const handleSelect = (event: SelectChangeEvent<string>) => {
    const key = event.target.value;
    const value = themeOptions.find((o) => o.key === key)?.value ?? Theme.Default;
    setTheme(value);
  };

  return (
    <Tooltip title={tooltip} placement={'left'}>
      <FormControl variant="standard" sx={{ minWidth: 'auto' }}>
        <Select
          variant="standard"
          sx={size === 'large' ? themeSelectorStyles.pickerLarge : themeSelectorStyles.picker}
          value={selectedKey}
          onChange={handleSelect}
          input={<Input disableUnderline />}
          renderValue={() => {
            const item = themeOptions.find((o) => o.key === selectedKey)!;
            return <ThemeSwatch option={item} width={triggerWidth} />;
          }}
          MenuProps={{ PaperProps: { sx: themeSelectorStyles.menuPaper } }}
        >
          {visibleOptions.map((option) => (
            <MenuItem value={option.key} key={option.key}>
              <ThemeSwatch option={option} width={menuWidth} />
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    </Tooltip>
  );
};

export default ThemeSelector;
